/**
 * Guided question save helpers — keeps GuidedQuestionCard under 200 lines.
 */
import { parseMoneyInput } from '../../../../lib/formatters'
import { localDateKey } from '../../../../lib/localDate'
import { saveEnabledForMoney } from './MoneyWordsConfirm'
import { CHIP_TO_CATEGORY } from './GuidedAnswerBody'
import type { GuidedQuestion } from '../../../../lib/costsMarginQuestions'
import type { QuestionSavePayload } from './types'

export async function saveGuidedPrimary(opts: {
  question: GuidedQuestion
  amount: string
  text: string
  chip: string
  wordsOk: boolean
  onNext: () => void
  onSkip: () => void
  onSave: (payload: QuestionSavePayload) => Promise<void>
  onShowDetail: () => void
  setDescFromText: () => void
  clearMoney: () => void
}): Promise<void> {
  const { question, amount, text, chip, wordsOk, onNext, onSkip, onSave, onShowDetail, setDescFromText, clearMoney } = opts
  const dollars = parseMoneyInput(amount)
  if (question.kind === 'contract') { onNext(); return }
  if (question.kind === 'subcontract_amount' && question.projectVendorId != null) {
    if (!saveEnabledForMoney(dollars, wordsOk)) return
    await onSave({ kind: 'subcontract_amount', projectVendorId: question.projectVendorId, amount_cents: Math.round(dollars * 100) })
    return
  }
  if (question.kind === 'labor' || question.kind === 'overhead') {
    if (!saveEnabledForMoney(dollars, wordsOk)) return
    await onSave({
      kind: 'job_cost',
      description: text.trim() || (question.kind === 'labor' ? 'Own labor' : 'Overhead / general conditions'),
      category: question.kind === 'labor' ? 'labor' : 'other',
      amount_cents: Math.round(dollars * 100),
      due_date: localDateKey(),
    })
    clearMoney()
    return
  }
  if (chip === 'Nothing else') { onSkip(); return }
  onShowDetail()
  setDescFromText()
}

export async function saveGuidedDetail(opts: {
  question: GuidedQuestion
  amount: string
  text: string
  desc: string
  vendor: string
  category: string
  chip: string
  wordsOk: boolean
  paid: boolean
  due: string
  projectVendorId: number | ''
  linkedOwnerCoId: number | ''
  subOptions: Array<{ id: number; label: string }>
  onSave: (payload: QuestionSavePayload) => Promise<void>
}): Promise<boolean> {
  const d = parseMoneyInput(opts.amount)
  if (!Number.isFinite(d) || d < 0 || !saveEnabledForMoney(d, opts.wordsOk)) return false
  if (opts.question.kind === 'sub_change_orders') {
    const pv = opts.projectVendorId === '' ? undefined : Number(opts.projectVendorId)
    const vendorName = opts.vendor.trim() || opts.subOptions.find((s) => s.id === pv)?.label
    if (pv == null && !vendorName) return false
    await opts.onSave({
      kind: 'sub_change_order',
      description: opts.desc.trim() || opts.text.trim() || 'Sub change order',
      amount_dollars: d,
      vendor_name: vendorName,
      project_vendor_id: pv,
      linked_change_order_id: opts.linkedOwnerCoId === '' ? null : Number(opts.linkedOwnerCoId),
    })
  } else {
    await opts.onSave({
      kind: 'job_cost',
      description: opts.desc.trim() || opts.text.trim() || 'Job cost',
      category: opts.category || CHIP_TO_CATEGORY[opts.chip] || 'other',
      amount_cents: Math.round(d * 100),
      vendor_name: opts.vendor.trim() || null,
      due_date: opts.paid ? null : (opts.due || localDateKey()),
      paid_at: opts.paid ? new Date().toISOString() : null,
    })
  }
  return true
}
